import disabled from 'platform/adapter/persistence/api/disabled'
import requests from 'platform/adapter/persistence/api/requests'
import files from 'platform/adapter/persistence/api/files'
import drafts from 'platform/adapter/persistence/api/drafts'
import settings from '@tenant/settings'

const apis = { requests, files, drafts }

const reduceAsync = async (array, callback) => {
  try {
    return await Object.values(array).reduce(async (promise, item) => {
      try {
        await promise
        return callback(item)
      } catch (e) {
        console.warn(e)
        return null
      }
    }, null)
  } catch (e) {
    console.warn(e)
    return null
  }
}

/**
 * @typedef {Function} DataServiceFactory
 * @param {StoreDescriptor} storeDescriptor
 * @returns {DataService}
 */

/**
 * @typedef {Object} DataService
 * @property {function(): Promise<void>} init
 * @property {function(number|string|array): Promise<any>} get
 * @property {function(Object = null): Promise<any>} getAll
 * @property {function(Object): Promise<Object>} put
 * @property {function(number|string|array): Promise<any>} remove
 * @property {function(Object = null): Promise<any>} removeAll
 */

/**
 * @typedef {Object} FileService
 * @property {function(Object, string): Promise<Blob>} get
 * @property {function(Object, string): Promise<void|boolean>} put
 * @property {function(Object, string): Promise<void|boolean>} remove
 */

/**
 * Persistence API
 *
 * Defines the methods to be exposed by the API object of the Persistence Adapter.
 *
 * @memberOf Gaia.Adapter.Persistence
 * @typedef {Object} PersistenceAPIs
 * @property {RequestsAPI} requests
 * @property {FilesAPI} files
 * @property {DraftsAPI} drafts
 */

/**
 * If the `suppressPersistence` setting is true, returns the {@link disabled} api, otherwise returns
 * `api`.
 *
 * @param {PersistenceAPI} api          the object to return if persistence is enabled
 * @returns {function(): PersistenceAPI} the corresponding persistence API
 */
const disabledOr = api => () => (settings.suppressPersistence ? disabled : api)

/**
 * Initializes the persistence storage by initializing one store after another.
 *
 * @returns {Promise<void>}
 */
const initialize = async () => {
  if (!settings.suppressPersistence) {
    await reduceAsync(apis, async api => await api.init())
    await reduceAsync(apis, async api => await api.clean())
  }
  return null
}

/**
 * Clears all persistence storages, leaving them empty.
 *
 * @returns {Promise<any|Promise<void>>}
 */
const clear = async () => {
  if (!settings.suppressPersistence) {
    await reduceAsync(apis, async api => await api.deleteAll())
  }
  return null
}

/**
 * Returns an implementation of {@link PersistenceAPIs}. Intended to abstract the use of the
 * underlying platform-dependent persistence mechanisms.
 *
 * {@see https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API}
 * @returns {PersistenceAPIs} api - storage api
 */
const api = () => Object.create({}, {
  init: {
    value: initialize,
    iterable: true,
    enumerable: true,
  },
  clear: {
    value: clear,
    iterable: true,
    enumerable: true,
  },
  ...Object.keys(apis).reduce((acc, name) => ({
    ...acc,
    [name]: {
      get: disabledOr(apis[name]),
      iterable: true,
      enumerable: true,
    },
  }), {}),
})

export default api
